import { useRef, useState, useEffect } from "react";

import { Button, Card, CardBody, Col, Label, Row } from "reactstrap";

//Unform e componentes
import { Form } from "@unform/web";
import UInput from "@/frontend/componentes/unform/UInput";
import UInputMask from "@/frontend/componentes/unform/UInputMask";
import USelect from "@/frontend/componentes/unform/USelect";
import UCheckbox from "@/frontend/componentes/unform/UCheckbox";
import URadio from "@/frontend/componentes/unform/URadio";
import TooltipItem from "@/frontend/componentes/TooltipItem";

//Common
import { TIPO_SOLICITACAO } from "@/common/constantes";
import {
  fn_addMonth,
  fn_getDataAtual,
  fn_getDataPrimeiroDiaMes,
  fn_getDataUltimoDiaMes,
  fn_getDataInicioPosicaoDia,
  fn_getMesAnoAtual,
  fn_validarData,
  fn_validarMesAno,
  fn_addDay,
} from "@/common/utils/date";

//Util
import { useEnterAsTab } from "@/frontend/utils/useEnterAsTab";
import { fn_validarCamposObrigatorios } from "@/frontend/utils/form";
import {
  fn_validarPeriodoData,
  fn_validarPeriodoMesAno,
} from "@/frontend/utils/util";
import { MENSAGENS_ERRO, showMensagemErro } from "@/frontend/utils/mensagens";
import { fn_loadAcessoArea } from "@/frontend/utils/siig/solicitacao";
import {
  fn_getCodigoGrupoContaDashboardReceita,
  fn_getNomeCampoGrupoContaDashboardReceita,
  fn_getOpcoesTipoDashboardUsuario,
  fn_getPeriodoQtdeDiasAntes,
  fn_loadOpcoesGrupoContaDashboardReceita,
  fn_loadOpcoesRadioGrupoContaDashboardReceita,
  GRUPO_CONTA_DASHBOARD_RECEITA_AMBOS,
} from "@/frontend/utils/siig/dashboard";

//Context
import { useAuth } from "@/frontend/context/auth";

//API
import { api_listar, ROTA_LISTAR } from "@/frontend/services/api/api_list";

//Telas
import TDashboard_pag from "./dashboard_pag";
import TDashboard_rec from "./dashboard_rec";

/*
 * Dashboard: escolha entre o dashboard de pagamentos e o de receitas (só quando o perfil tem acesso aos dois)
 * e os filtros de cada um. Os cards ficam em dashboard_pag.jsx e dashboard_rec.jsx.
 * Spec: documentos/specs/2026-10-24-dashboard-receitas.md
 */
function TDashboard() {
  const { sessaoUsuario } = useAuth();

  //### USE REF ###
  const formRef = useRef(null);
  const domRef = useRef(null); // ref do wrapper DOM
  useEnterAsTab(formRef, domRef);

  //### USE STATE ###
  //Dashboards que o usuário pode ver (pagamentos e/ou receitas)
  const [opcoesTipoDashboard] = useState(() =>
    fn_getOpcoesTipoDashboardUsuario(sessaoUsuario),
  );
  const [tipoDashboard, setTipoDashboard] = useState(
    () => opcoesTipoDashboard[0].value,
  );

  const [opcoesAreaFull, setOpcoesAreaFull] = useState([]);
  const [opcoesAreaAcesso, setOpcoesAreaAcesso] = useState([]);

  const [datasFeriados, setDatasFeriados] = useState(null);
  const [checkPosicaoDia, setCheckPosicaoDia] = useState(false);
  const [checkQtdeDiasAntes, setCheckQtdeDiasAntes] = useState(false);

  //Filtros da última consulta (com o tipo do dashboard) e a chave da consulta, passados para a tela dos cards
  const [filtrosConsulta, setFiltrosConsulta] = useState(null);
  const [keyConsulta, setKeyConsulta] = useState(0);

  const [keyAtualizarPage, setKeyAtualizarPage] = useState(0);

  //### VARIÁVEIS AUXILIARES ###
  const isReceita = tipoDashboard == TIPO_SOLICITACAO.RECEITA;

  //Período padrão da receita: o mês/ano atual
  const mesAnoAtual = fn_getMesAnoAtual();

  //### USE EFFECT ###
  //Inicialização da tela
  useEffect(() => {
    //Lista os feriados entre a data atual e 7 dias antes
    async function listarFeriadoPeriodo() {
      const dataAtual = fn_getDataAtual();
      const dataInicio = fn_addDay(dataAtual, -7);
      const dataFim = dataAtual;

      const feriados = await api_listar(
        ROTA_LISTAR.FERIADO_PERIODO,
        `data_inicio=${dataInicio}&data_fim=${dataFim}&afeta_pagamentos=S`, //&id_uf=${idUF}&id_cidade=${idCidade}
      );

      setDatasFeriados(feriados.map((feriado) => feriado.data));
    }

    listarFeriadoPeriodo();
  }, []);

  //Ao carregar os feriados e ao trocar o dashboard: filtros com os valores padrão do dashboard e consulta
  useEffect(() => {
    if (datasFeriados != null) {
      //Pagamento: posição do dia marcada e qtde dias antes desmarcada (a área escolhida é mantida)
      if (tipoDashboard == TIPO_SOLICITACAO.PAGAMENTO) {
        setCheckPosicaoDia(true);
        setCheckQtdeDiasAntes(false);
        definirPeriodo(true, datasFeriados);
      }

      //aguarda setar a posição do dia para acionar a consulta
      const timer = setTimeout(() => {
        setKeyAtualizarPage((prev) => prev + 1);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [datasFeriados, tipoDashboard]);

  //Ao selecionar a posição do dia
  useEffect(() => {
    if (tipoDashboard == TIPO_SOLICITACAO.PAGAMENTO && !checkQtdeDiasAntes) {
      definirPeriodo(checkPosicaoDia, datasFeriados);
    }
  }, [checkPosicaoDia]);

  //Ao selecionar a qtde dias antes: marcado, o período vem da qtde de dias (se já informada);
  //desmarcado (e a posição do dia também), volta o período padrão
  useEffect(() => {
    if (tipoDashboard == TIPO_SOLICITACAO.PAGAMENTO) {
      if (checkQtdeDiasAntes) {
        definirPeriodoQtdeDiasAntes();
        //O campo da qtde só é habilitado na próxima renderização
        setTimeout(() => {
          formRef?.current?.getFieldRef("qtde_dias_antes")?.current?.focus();
        }, 100);
      } else if (!checkPosicaoDia) {
        definirPeriodo(false, datasFeriados);
      }
    }
  }, [checkQtdeDiasAntes]);

  //Consulta: carrega as áreas de acesso e passa os filtros para a tela dos cards
  useEffect(() => {
    if (keyAtualizarPage > 0) {
      async function consultar() {
        try {
          const body = formRef.current.getData();

          const possuiPeriodo = isReceita
            ? body.mes_ano_inicio && body.mes_ano_fim
            : body.data_inicio && body.data_fim;

          //Receita sem grupo da conta marcado não consulta (sem o filtro, traria as receitas de todos os grupos)
          // const possuiGrupoConta =
          //   !isReceita || fn_getCodigoGrupoContaDashboardReceita(body) != "";
          const possuiGrupoConta = !isReceita || !!body.codigo_grupo_conta;

          if (possuiPeriodo && possuiGrupoConta) {
            const { opcoesAreaFull, opcoesAreaAcesso } =
              await fn_loadAcessoArea(sessaoUsuario);

            setOpcoesAreaFull(opcoesAreaFull);
            setOpcoesAreaAcesso(opcoesAreaAcesso);

            //Sem área escolhida: todas as áreas de acesso do perfil
            let idsArea = "";
            if (body.solicitacao_id_area?.length == 0) {
              idsArea = opcoesAreaAcesso.map((area) => area.value).join(",");
            } else {
              idsArea = body.solicitacao_id_area.join(",");
            }

            setFiltrosConsulta(
              isReceita
                ? {
                    tipo: tipoDashboard,
                    mes_ano_inicio: body.mes_ano_inicio,
                    mes_ano_fim: body.mes_ano_fim,
                    ids_area: idsArea,
                    // codigo_grupo_conta:
                    //   fn_getCodigoGrupoContaDashboardReceita(body),
                    codigo_grupo_conta: body.codigo_grupo_conta,
                  }
                : {
                    tipo: tipoDashboard,
                    data_inicio: body.data_inicio,
                    data_fim: body.data_fim,
                    ids_area: idsArea,
                  },
            );
            setKeyConsulta((prev) => prev + 1);
          }
        } catch (error) {
          showMensagemErro(
            MENSAGENS_ERRO.TENTATIVA_OPERACAO,
            "carregar dashboard",
          );
        }
      }

      consultar();
    }
  }, [keyAtualizarPage]);

  //### FUNÇÕES ###
  //Define o período da consulta do pagamento
  function definirPeriodo(posicaoDia, datasFeriados) {
    if (posicaoDia) {
      const dataAtual = fn_getDataAtual();
      const dataInicioDia = fn_getDataInicioPosicaoDia(
        dataAtual,
        datasFeriados,
      );
      const dataFimDia = dataAtual;

      formRef.current.setFieldValue("data_inicio", dataInicioDia);
      formRef.current.setFieldValue("data_fim", dataFimDia);
    } else {
      const mesAnoAtual = fn_getMesAnoAtual();
      formRef.current.setFieldValue(
        "data_inicio",
        fn_getDataPrimeiroDiaMes(fn_addMonth(mesAnoAtual, -1)),
      );
      formRef.current.setFieldValue(
        "data_fim",
        fn_getDataUltimoDiaMes(mesAnoAtual),
      );
    }
  }

  //Define o período da consulta do pagamento pela qtde dias antes informada (sem a qtde válida, mantém o período)
  //Retorna se definiu o período
  function definirPeriodoQtdeDiasAntes() {
    const periodo = fn_getPeriodoQtdeDiasAntes(
      fn_getDataAtual(),
      formRef.current.getFieldValue("qtde_dias_antes"),
    );

    if (periodo) {
      formRef.current.setFieldValue("data_inicio", periodo.data_inicio);
      formRef.current.setFieldValue("data_fim", periodo.data_fim);
    }
    return periodo != null;
  }

  //Valida os dados do formulário
  function validarFormulario(body) {
    let isValido = fn_validarCamposObrigatorios(formRef, body, [
      "solicitacao_id_area",
      "tipo_dashboard",
      //A qtde dias antes só é obrigatória com o check marcado
      ...(isReceita || !checkQtdeDiasAntes ? ["qtde_dias_antes"] : []),
    ]);

    //Pagamento com a qtde dias antes: a qtde precisa ser maior do que zero (o período é calculado por ela)
    if (isValido && !isReceita && checkQtdeDiasAntes) {
      if (!definirPeriodoQtdeDiasAntes()) {
        showMensagemErro(
          MENSAGENS_ERRO.CAMPO_X_MAIOR_DO_QUE_Y,
          "Próximos dias a vencer",
          "zero",
        );
        formRef.current.setFieldError("qtde_dias_antes", "inválido");
        isValido = false;
      }
    }

    //Receita: pelo menos um grupo da conta marcado
    //(com o radio do grupo da conta sempre há uma opção marcada, por isso não precisa da validação dos checks)
    // if (
    //   isValido &&
    //   isReceita &&
    //   !fn_getCodigoGrupoContaDashboardReceita(body)
    // ) {
    //   showMensagemErro(MENSAGENS_ERRO.SELECIONE_PELOMENOSUM, "grupo da conta");
    //   isValido = false;
    // }

    if (isValido) {
      isValido = isReceita
        ? fn_validarPeriodoMesAno(
            formRef,
            "mes_ano_inicio",
            "mes_ano_fim",
            true,
            12,
          )
        : fn_validarPeriodoData(formRef, "data_inicio", "data_fim", true, 365);
    }
    return isValido;
  }

  //### EVENTOS ###
  //OnChange - Pagamentos / Receitas
  function handleChangeTipoDashboard(event) {
    setTipoDashboard(event.target.value);
  }

  //OnBlur - Data
  function handleBlurData(event) {
    const valorCampo = event.target.value;

    if (valorCampo && !fn_validarData(valorCampo)) {
      showMensagemErro(MENSAGENS_ERRO.DATA_INVALIDA);
      event.target.focus();
    }
  }

  //OnBlur - Mês/ano
  function handleBlurMesAno(event) {
    const valorCampo = event.target.value;

    if (valorCampo && !fn_validarMesAno(valorCampo)) {
      showMensagemErro(MENSAGENS_ERRO.MESANO_INVALIDO);
      event.target.focus();
    }
  }

  //OnClick - Check Posição dia (marcado, desmarca a qtde dias antes)
  function handleClickCheckPosicaoDia(event) {
    if (event.target.checked) {
      formRef.current.setFieldValue("chkQtdeDiasAntes", false);
      setCheckQtdeDiasAntes(false);
    }
    setCheckPosicaoDia(event.target.checked);
  }

  //OnClick - Check Qtde dias antes (marcado, desmarca a posição do dia)
  function handleClickCheckQtdeDiasAntes(event) {
    if (event.target.checked) {
      formRef.current.setFieldValue("chkDoDia", false);
      setCheckPosicaoDia(false);
    }
    setCheckQtdeDiasAntes(event.target.checked);
  }

  //OnBlur - Qtde dias antes: recalcula o período
  function handleBlurQtdeDiasAntes() {
    definirPeriodoQtdeDiasAntes();
  }

  //OnClick - Check do grupo da conta (receita): já consulta, com a mesma validação do botão Consultar
  //(sem nenhum grupo marcado, avisa e não consulta)
  // function handleClickCheckGrupoConta() {
  //   handleBotaoConsultar();
  // }

  //OnChange - Grupo da conta (receita): já consulta, com a mesma validação do botão Consultar
  function handleChangeGrupoConta() {
    handleBotaoConsultar();
  }

  //OnClick - Botão Consultar
  function handleBotaoConsultar() {
    const body = formRef.current.getData();

    if (validarFormulario(body)) {
      setKeyAtualizarPage((prev) => prev + 1);
    }
  }

  return (
    <div className="m-3" ref={domRef}>
      <div className="titulo-tela">Dashboard Gestora</div>

      <Card className="mt-2">
        <CardBody>
          <Form ref={formRef}>
            {/* Escolha do dashboard (só quando o perfil tem acesso aos pagamentos e às receitas) na coluna dos títulos e,
                na receita, o grupo da conta ao lado, alinhado com os campos das linhas de baixo.
                Os dois já consultam ao clicar, por isso ficam separados dos filtros que dependem do botão Consultar */}
            {(opcoesTipoDashboard.length > 1 || isReceita) && (
              <Row className="mb-3 align-items-center">
                <Col sm="2" md="2" lg="2">
                  {(opcoesTipoDashboard.length > 1 && (
                    //pularLinha: sem espaço na coluna, as opções passam para a linha de baixo
                    <URadio
                      name="tipo_dashboard"
                      data={opcoesTipoDashboard}
                      value={tipoDashboard}
                      onChange={handleChangeTipoDashboard}
                      pularLinha={true}
                    />
                  )) ||
                    //Só com acesso às receitas (sem a escolha): o título do grupo da conta fica na coluna dos títulos
                    (isReceita && (
                      <Label className="mb-2 text-nowrap">Grupo conta*</Label>
                    ))}
                </Col>

                {isReceita && (
                  <Col className="d-flex flex-wrap align-items-center">
                    {opcoesTipoDashboard.length > 1 && (
                      <Label className="mb-0 ms-1 mb-2 me-3">
                        Grupo conta*
                      </Label>
                    )}
                    {/* {fn_loadOpcoesGrupoContaDashboardReceita().map((opcao) => (
                      <div
                        key={opcao.value}
                        className="d-flex align-items-center me-4"
                      >
                        <UCheckbox
                          name={fn_getNomeCampoGrupoContaDashboardReceita(
                            opcao.value,
                          )}
                          label={opcao.label}
                          defaultChecked={true}
                          onClick={handleClickCheckGrupoConta}
                        />
                        {opcao.textTip && (
                          <TooltipItem text={opcao.textTip} showImageHelp />
                        )}
                      </div>
                    ))} */}

                    {/* Os dois grupos e "Ambos" (padrão): sempre há uma opção marcada */}
                    <URadio
                      name="codigo_grupo_conta"
                      data={fn_loadOpcoesRadioGrupoContaDashboardReceita()}
                      defaultChecked={GRUPO_CONTA_DASHBOARD_RECEITA_AMBOS}
                      onChange={handleChangeGrupoConta}
                      pularLinha={true}
                    />
                  </Col>
                )}
              </Row>
            )}

            {(isReceita && (
              <Row className="mb-2">
                <Col sm="2" md="2" lg="2">
                  <Label className="mt-2 text-nowrap">
                    Mês/ano vencimento*
                  </Label>
                </Col>
                <Col>
                  <div className="d-flex">
                    <UInputMask
                      tipoMask="mesano"
                      name="mes_ano_inicio"
                      showCalendario={true}
                      defaultValue={mesAnoAtual}
                      onBlur={handleBlurMesAno}
                      onFocus={(event) => event.target.select()}
                    />
                    <span className="mt-2 ms-2 me-2">a</span>
                    <UInputMask
                      tipoMask="mesano"
                      name="mes_ano_fim"
                      showCalendario={true}
                      defaultValue={mesAnoAtual}
                      onBlur={handleBlurMesAno}
                      onFocus={(event) => event.target.select()}
                    />
                  </div>
                </Col>
              </Row>
            )) || (
              <Row className="mb-2">
                <Col sm="2" md="2" lg="2">
                  <Label className="mt-2 text-nowrap">Data vencimento*</Label>
                </Col>
                <Col>
                  <div className="d-flex align-items-center">
                    <div className="d-flex">
                      <UInputMask
                        tipoMask="data"
                        name="data_inicio"
                        onBlur={handleBlurData}
                        onFocus={(event) => event.target.select()}
                        disabled={checkPosicaoDia || checkQtdeDiasAntes}
                      />
                      <span className="mt-2 ms-2 me-2">a</span>
                      <UInputMask
                        tipoMask="data"
                        name="data_fim"
                        onBlur={handleBlurData}
                        onFocus={(event) => event.target.select()}
                        disabled={checkPosicaoDia || checkQtdeDiasAntes}
                      />

                      <span className="ms-2"></span>
                      <span className="mt-2">
                        <UCheckbox
                          name="chkDoDia"
                          label="Posição do dia"
                          defaultChecked={true}
                          onClick={handleClickCheckPosicaoDia}
                        />
                      </span>

                      {/* Qtde dias antes: o período vai da data atual até a data atual mais a qtde de dias */}
                      <span className="ms-3"></span>
                      <span className="mt-2">
                        <UCheckbox
                          name="chkQtdeDiasAntes"
                          label="Próximos dias a vencer"
                          onClick={handleClickCheckQtdeDiasAntes}
                        />
                      </span>
                      <UInput
                        name="qtde_dias_antes"
                        tipo_valor="N"
                        maxLength={3}
                        className="input-qtde"
                        aria-label="Próximos dias a vencer"
                        onBlur={handleBlurQtdeDiasAntes}
                        disabled={!checkQtdeDiasAntes}
                      />
                    </div>
                  </div>
                </Col>
              </Row>
            )}

            <Row className="mb-2">
              <Col sm="2" md="2" lg="2">
                <Label className="mt-3">Área*</Label>
              </Col>
              <Col sm="8" md="8" lg="8">
                <div className="d-flex align-items-center">
                  <div className="w-100">
                    <USelect
                      name="solicitacao_id_area"
                      isMulti={true}
                      isSearchable={true}
                      placeholder={
                        opcoesAreaAcesso.length == opcoesAreaFull.length
                          ? "TODAS"
                          : "TODAS DO PERFIL"
                      }
                      options={opcoesAreaAcesso}
                    />
                  </div>

                  <span className="ms-2"></span>

                  <Button
                    name="btnConsultar"
                    color="primary"
                    onClick={handleBotaoConsultar}
                  >
                    Consultar
                  </Button>
                </div>
              </Col>

              <Col></Col>
            </Row>

            {/* {isReceita && (
              <Row className="mb-2">
                <Col sm="2" md="2" lg="2">
                  <Label className="mt-1">Grupo conta*</Label>
                </Col>
                <Col className="d-flex flex-wrap align-items-center">
                  {fn_loadOpcoesGrupoContaDashboardReceita().map((opcao) => (
                    <div
                      key={opcao.value}
                      className="d-flex align-items-center me-4"
                    >
                      <UCheckbox
                        name={fn_getNomeCampoGrupoContaDashboardReceita(
                          opcao.value,
                        )}
                        label={opcao.label}
                        defaultChecked={true}
                      />
                      {opcao.textTip && (
                        <TooltipItem text={opcao.textTip} showImageHelp />
                      )}
                    </div>
                  ))}
                </Col>
              </Row>
            )} */}
          </Form>
        </CardBody>
      </Card>
      <br />

      {/* Cards do dashboard escolhido, com os filtros da última consulta dele */}
      {filtrosConsulta?.tipo == tipoDashboard &&
        (isReceita ? (
          <TDashboard_rec
            filtros={filtrosConsulta}
            keyConsulta={keyConsulta}
            datasFeriados={datasFeriados}
          />
        ) : (
          <TDashboard_pag filtros={filtrosConsulta} keyConsulta={keyConsulta} />
        ))}
    </div>
  );
}

export default TDashboard;
